import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Stack,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { Pie, Doughnut } from "react-chartjs-2";
import { ChartOptions, ChartData } from "chart.js";
import "chart.js/auto";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useNavigate } from "react-router-dom";
import Text from "@mui/material/Typography";
import { MathJax } from "better-react-mathjax";
import IdIcon from "../../assets/img/id.png";
import {
  profileDDRStatistics,
  profileDetailsAPI,
  profileStatistics,
} from "../../api/userApi";
import { useAppSelector } from "../../store/hooks";
import { getRecommendedArticles } from "../../api/MyLibrary/MyFolders.api";
import { GetFolderArticlesData } from "../../types/GetMyFolders.types";
import {
  getBulkLinkRecords,
  LinkResolverType,
} from "../../api/linkResolverApi";
import { Divider } from "@mui/material";
import { keywordHighlight } from "../../utils/highlightKeyword";
import metaData from "../../utils/metadata";
import { authorRole, Colors, dataTypes } from "../../utils/constants";
import {
  isStringOfArray,
  sortAuthor,
  sortAuthorDetail,
  sortByAuthorRole,
} from "../../utils/helper";

const generateRandomColors = (count: number) => {
  const startHue = Math.floor(Math.random() * 360); // RANDOM start

  return Array.from({ length: count }, (_, i) => {
    const hue = (startHue + Math.round((360 / count) * i)) % 360;
    return `hsl(${hue}, 70%, 55%)`;
  });
};

const buildIllData = (data: any[]) => {
  const request = data?.[0]?.request_count ?? 0;
  const fulfilled = data?.[0]?.fulfiled_count ?? 0;

  return {
    labels: [`Requested (${request})`, `Fulfilled (${fulfilled})`],
    datasets: [
      {
        data: [request, fulfilled],
        backgroundColor: [
          "#9E9E9E", // Requested (gray)
          "#4CAF50", // Fulfilled (green)
        ],
      },
    ],
  };
};

const ProfileDashboard: React.FC = () => {
  const userID = sessionStorage.getItem("user_id") || "";
  const customerId = sessionStorage.getItem("informaticscustomer_id") || "";
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("md"));
  const Xs = useMediaQuery(theme.breakpoints.down("sm"));

  const isSM = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const legendFontSize = isXs ? 12 : isMd ? 12 : 14;

  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState<any>({});
  const [activeTab, setActiveTab] = useState(0);
  const [usageSumarry, setUsageSumarry] = useState<any>(null);
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedDDRYear, setSelectedDDRYear] = useState<number>(currentYear);
  const [ddrUsageSumarry, setDDRUsageSumarry] =
    useState<ChartData<"doughnut"> | null>(null);
  const [articles, setArticles] = useState<GetFolderArticlesData>();
  const [linkToDisplay, setLinkToDisplay] = useState<LinkResolverType[] | null>(
    null,
  );

  const hasFetched = useRef(false);

  const data_types = useAppSelector((state) => state.searchReducer.dataType);
  const l2Subjects = useAppSelector((state) => state.searchReducer.subjectType);
  const consortiaId = useAppSelector(
    (state) => state.customer.customerDetails?.consortiamaster_id,
  );

  const pieOptions: ChartOptions<"pie"> = {
    plugins: {
      legend: {
        display: true,
        position: "right",
        labels: { color: "#333", font: { size: legendFontSize } },
      },
      tooltip: { enabled: true },
    },
    maintainAspectRatio: false,
  };

  const doughnutOptions: ChartOptions<"doughnut"> = {
    cutout: "70%",
    plugins: {
      legend: {
        display: true,
        position: "right",
        labels: { color: "#333", font: { size: legendFontSize } },
      },
      tooltip: { enabled: true },
    },
    maintainAspectRatio: false,
  };

  const usagePieData = React.useMemo<ChartData<"pie"> | null>(() => {
    if (!usageSumarry || !Array.isArray(usageSumarry)) return null;

    const filtered = usageSumarry.filter(
      (item: any) => !Object.keys(item)[0].includes("TOTAL"),
    );

    const labels = filtered.map((obj: any) => {
      const key = Object.keys(obj)[0];
      const value = Number(obj[key]);
      return `${key} (${value})`;
    });
    const values = filtered.map((obj: any) => Number(Object.values(obj)[0]));

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: generateRandomColors(values.length),
          borderColor: "#ffffff",
          borderWidth: 1,
        },
      ],
    };
  }, [usageSumarry]);

  useEffect(() => {
    const fetchUserData = async () => {
      const data = await profileDetailsAPI({ userid: userID });
      setUserData(data?.data?.data[0][0]);
    };
    fetchUserData();
  }, [userID]);

  useEffect(() => {
    const fetchUserStatisticData = async () => {
      const data = await profileStatistics({
        customerid: customerId,
        userid: userID,
        year: selectedYear,
        consortia_id: consortiaId,
      });
      setUsageSumarry(data?.data?.data[0][0]?.usage_sumarry);
    };
    fetchUserStatisticData();
  }, [selectedYear]);

  useEffect(() => {
    const fetchUserDDRStatisticData = async () => {
      const data = await profileDDRStatistics({
        customerid: customerId,
        userid: userID,
        // customerid: 3211,
        // userid: 33168,
        year: selectedDDRYear,
      });

      const illStats = data?.data?.data?.[0] ?? [
        { request_count: 0, fulfiled_count: 0 },
      ];
      setDDRUsageSumarry(buildIllData(illStats));
    };
    fetchUserDDRStatisticData();
  }, [selectedDDRYear]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const fetchRecommendedArticles = async () => {
      setLoading(true);

      try {
        const res = await getRecommendedArticles(
          userID,
          customerId,
          l2Subjects,
          data_types,
        );
        const data = res?.data?.data ?? [];
        const docs = data?.docs || [];
        setArticles(data);
        fetchLinkDetails(docs);
      } catch (error) {
        console.error("Failed to fetch recommended articles", error);
        //setArticles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRecommendedArticles();
  }, [l2Subjects, data_types]);

  async function fetchLinkDetails(arr: any) {
    if (!Array.isArray(arr)) return;
    let formData = new FormData();

    const mapped = arr.map((obj: any) => {
      let object: any = {
        customer_id: customerId,
        article_id: obj?.article_id,
        resource_type: obj?.resource_type,
        access_type: obj?.article_open_status || null,
        is_oa_article: obj?.fulltext || false,
      };
      if (obj?.articledoi) object.article_doi = obj?.articledoi;
      if (obj?.resourceissue_id)
        object.resourceissue_id = obj?.resourceissue_id;
      if (obj?.consortia_virtualid)
        object.consortia_virtualid = obj?.consortia_virtualid;
      return object;
    });

    formData.append("detailed_json", JSON.stringify({ data: mapped }));
    const response = await getBulkLinkRecords(formData);
    if (response?.message === "Ok") setLinkToDisplay(response.data);
  }

  const handleAbstractFullScreen = (data?: any) => {
    navigate("/abstractFullScreen", { state: { data, linkToDisplay } });
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setSelectedYear(Number(event.target.value));
  };

  const handleDDRYearChange = (event: SelectChangeEvent<number>) => {
    setSelectedDDRYear(Number(event.target.value));
  };

  return (
    <Box sx={{ p: 2, background: "#f5f7fa", minHeight: "100vh" }}>
      <Grid container spacing={2}>
        {/* ================= LEFT COLUMN ================= */}
        <Grid item xs={12} md={4.5}>
          <Grid container spacing={2}>
            {/* ---------- Profile Card ---------- */}
            <Grid item xs={12}>
              <Card
                sx={{
                  p: 1,
                  borderRadius: 2,
                  boxShadow: 3,
                  height: 240,
                }}
              >
                <CardContent
                  sx={{
                    overflowY: "auto",
                    height: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 3 }}>
                    <Box sx={{ alignItems: "center" }}>
                      <Avatar
                        src={userData?.profileuserimagepath || ""}
                        variant="rounded"
                        sx={{ width: 120, height: 120 }}
                      />
                      <Typography fontSize={14}>JGate ID: {userID}</Typography>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography fontSize={14} fontWeight={600}>
                        {[userData?.firstname, userData?.lastname]
                          .filter(Boolean)
                          .join(" ")}

                        <Tooltip title="Edit Profile">
                          <IconButton
                            size="small"
                            sx={{ ml: 1 }}
                            onClick={() => navigate("/profile")}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Typography>

                      {userData?.profile_type &&
                        userData?.profile_type !== "Others" && (
                          <Typography color="333" fontSize={14}>
                            {userData.profile_type}
                          </Typography>
                        )}

                      {userData?.emailid && (
                        <Typography fontSize={14} color="#333">
                          {userData.emailid}
                        </Typography>
                      )}
                      {userData?.mobile && (
                        <Typography fontSize={14} color="#333">
                          {userData.mobile}
                        </Typography>
                      )}
                      {userData?.department && (
                        <Typography fontSize={14} color="#333">
                          Department: {userData.department}
                        </Typography>
                      )}
                      {userData?.designation &&
                        userData?.profile_type === "Others" && (
                          <Typography fontSize={14} color="#333">
                            Designation: {userData.designation}
                          </Typography>
                        )}

                      {userData?.area_of_interest && (
                        <Typography fontSize={14} color="#333">
                          Area of Interest: {userData.area_of_interest}
                        </Typography>
                      )}
                      {userData?.libraryidnumber && (
                        <Typography fontSize={14} color="#333">
                          Library ID: {userData.libraryidnumber}
                        </Typography>
                      )}
                      {userData?.orcid && (
                        <Typography fontSize={14} color="#333">
                          ORCID: {userData.orcid}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card
                sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 3,
                  height: 320,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography>J-Gate Usage Overview</Typography>

                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select value={selectedYear} onChange={handleYearChange}>
                      {yearOptions.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <Box sx={{ height: 230 }}>
                  {usagePieData ? (
                    <Pie data={usagePieData} options={pieOptions} />
                  ) : (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>

            {consortiaId ? (
              <Grid item xs={12}>
                <Card sx={{ p: 2, borderRadius: 2, boxShadow: 3, height: 320 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography>
                      Document Request & Fulfillment Overview
                    </Typography>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={selectedDDRYear}
                        onChange={handleDDRYearChange}
                      >
                        {yearOptions.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Box sx={{ height: 230, mt: 2 }}>
                    {ddrUsageSumarry ? (
                      <Doughnut
                        data={ddrUsageSumarry}
                        options={doughnutOptions}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    )}
                  </Box>
                </Card>
              </Grid>
            ) : null}
          </Grid>
        </Grid>

        {/* ================= RIGHT COLUMN ================= */}

        <Grid item xs={12} md={7.5}>
          <Card
            sx={{
              p: 2,
              borderRadius: 2,
              boxShadow: 3,
              // height: "calc(100vh - 32px)",
              overflowY: "auto",
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab label="Recommended Articles" />
                {userData?.isauthorflag && <Tab label="Published Articles" />}
              </Tabs>
            </Box>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <CircularProgress size={22} />
              </Box>
            )}

            {activeTab === 0 && articles?.docs && articles.docs.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {articles.docs.map((article, index) => {
                  const VALID_DATA_TYPES = new Set([
                    dataTypes.journalArticle,
                    dataTypes.journals,
                    dataTypes.bookSeries,
                    dataTypes.dissertationThesis,
                    dataTypes.preprint,
                    dataTypes.conferenceProceedings,
                    dataTypes.magazine,
                    dataTypes.trade,
                    dataTypes.books,
                    dataTypes.dataset,
                    dataTypes.dspaceThesis,
                    dataTypes.dspaceBookChapter,
                    dataTypes.dspaceBook,
                    dataTypes.dspaceDataTypeNull,
                    dataTypes.patents,
                    dataTypes.ncgVideo,
                    dataTypes.audioVideo,
                  ]);

                  const MAX_AUTHORS = isXs ? 1 : 3;

                  const isValidContent = VALID_DATA_TYPES.has(
                    article?.data_type,
                  );

                  const rawAuthors =
                    article?.data_type === dataTypes.books
                      ? article?.authors_tk
                      : article?.author_details;
                  const authors = Array.isArray(rawAuthors)
                    ? isStringOfArray(rawAuthors)
                      ? sortAuthor(rawAuthors)
                      : sortByAuthorRole(
                          sortAuthorDetail(rawAuthors),
                          article?.data_type === dataTypes.dissertationThesis,
                        )
                    : [];
                  return (
                    <Card
                      key={article.article_id ?? index}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: 1,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: 4,
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      {/* ---------- Title ---------- */}
                      <Typography
                        fontWeight={600}
                        fontFamily="Lora"
                        sx={{
                          color: Colors.primary,
                          fontSize: "0.8rem",
                          cursor: "pointer",
                          "&:hover": { color: "#1976d2" },
                        }}
                        onClick={() => handleAbstractFullScreen(article)}
                      >
                        <MathJax>
                          <div
                            id="title"
                            dangerouslySetInnerHTML={{
                              __html: keywordHighlight(
                                article,
                                [],
                                article?.article_id,
                                article?.title,
                              ),
                            }}
                          />
                        </MathJax>
                      </Typography>

                      {/* ---------- Meta Data ---------- */}

                      {isValidContent && (
                        <CardContent
                          sx={{
                            display: "flex",
                            padding: "0 14px",
                            alignItems: "center",
                            fontSize: "0.7rem",
                          }}
                        >
                          {authors
                            .slice(0, MAX_AUTHORS + 1)
                            .map((author: any, i) => {
                              if (i > MAX_AUTHORS) return null;

                              const first = author?.author_fname ?? "";
                              const last = author?.author_lname ?? "";
                              const displayName = [last, first]
                                .filter(Boolean)
                                .join(", ");
                              const reversedName = last
                                ? `${last}, ${first}`
                                : first;

                              return (
                                <Box
                                  key={i}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  flexDirection={"row"}
                                >
                                  {i < MAX_AUTHORS ? (
                                    <>
                                      <Tooltip
                                        title={
                                          authorRole[author?.datauthorrole_id]
                                        }
                                      >
                                        <Button
                                          sx={{
                                            fontSize: "0.7rem",
                                            textDecoration: "underline",
                                            padding:
                                              i === 0 ? "8px 0 6px" : "6px 8px",
                                            cursor: "default",
                                            color: "#6B7280",
                                          }}
                                        >
                                          {displayName}
                                        </Button>
                                      </Tooltip>

                                      {author?.orc_id && (
                                        <a
                                          href={`https://orcid.org/${author.orc_id}`}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          <img src={IdIcon} alt="ORCID" />
                                        </a>
                                      )}

                                      <Divider
                                        orientation="vertical"
                                        flexItem
                                        sx={{
                                          mx: 1,
                                          height: "20px",
                                          alignSelf: "center",
                                        }}
                                      />
                                    </>
                                  ) : (
                                    <Button
                                      sx={{
                                        color: "#6B7280",
                                        textDecoration: "underline",
                                        fontSize: "0.7rem",
                                      }}
                                      onClick={() =>
                                        handleAbstractFullScreen(article)
                                      }
                                    >
                                      +{authors.length - MAX_AUTHORS} More
                                    </Button>
                                  )}
                                </Box>
                              );
                            })}
                        </CardContent>
                      )}

                      {/* <CardContent */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          padding: "4px 14px",
                          alignItems: { xs: "start", sm: "center" },
                        }}
                      >
                        <Text
                          component={"div"}
                          sx={{
                            textAlign: "left",
                            fontSize: "0.8rem",
                            fontStyle: "italic",
                            cursor: "default",
                          }}
                        >
                          {metaData(article)}
                        </Text>
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              !loading && (
                <Typography
                  sx={{ textAlign: "center", color: "text.secondary", mt: 2 }}
                >
                  No data available
                </Typography>
              )
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfileDashboard;
